'use client';

import { Header } from '@portals/external-services-header';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { ButtonInterface, ButtonSecondary } from '@portals/ui-kit';
import { useParams } from 'next/navigation';
import { TSelectOption } from '@portals/external-services-header/lib/_components/header/_types';
import {
  APPEAL_TRANSLATIONS,
  TRANSLATION_NAMESPACE,
} from '@/_constants/translations';
import { redirectToMainUrl } from '@/_utils/redirect-to-main-url';
import { LOCALES } from '@/_constants';
import { Logo } from '../logo';
import { LANGUAGES_LIST } from './_constants';
import { getHeaderPopupLinks } from './_utils/get-header-popup-links';
import { useLanguage } from './_hooks/use-language';

const CustomHeader = () => {
  const translations = useTranslations(TRANSLATION_NAMESPACE);
  const params = useParams<{ locale: string }>();
  const { changeLanguage } = useLanguage();

  const locale = params.locale || LOCALES.RU;

  const headerPopupLinks = useMemo(() => getHeaderPopupLinks(locale), [locale]);

  const selectedLanguage = useMemo(
    () => LANGUAGES_LIST?.find(({ id }) => locale === id),
    [locale],
  );

  const headerMenuOptions = headerPopupLinks.map(
    ({ id, title, routeName }) => ({
      id,
      title: translations(title),
      routeName,
    }),
  );

  const handleMenuItemClick = (item: TSelectOption): void => {
    window.open(item.routeName, '_blank');
  };

  const desktopMenuParams = headerMenuOptions.length
    ? {
        desktop: {
          onClick: handleMenuItemClick,
          options: headerMenuOptions,
        },
      }
    : undefined;

  return (
    <Header
      actionButtons={{
        firstButton: {
          text: translations(APPEAL_TRANSLATIONS.header.login),
          color: 'off-white',
          onClick: redirectToMainUrl,
        },
        secondButton: {
          text: translations(APPEAL_TRANSLATIONS.header.register),
          onClick: redirectToMainUrl,
          color: 'secondary',
        },
      }}
      createInstance
      customLogo={<Logo />}
      languageSelectParams={
        !!LANGUAGES_LIST?.length
          ? {
              options: LANGUAGES_LIST,
              onChange: changeLanguage,
              value: selectedLanguage?.id,
            }
          : undefined
      }
      menuParams={{
        ...desktopMenuParams,
        mobile: {
          options: headerMenuOptions,
          onClick: handleMenuItemClick,
          tertiaryLine: {
            sbActions: {
              Layout: 'Vertical',
              isButtonFullWidth: true,
              ActionWidth: 'Full',
              firstCustomItem: (
                <ButtonSecondary
                  fullWidth
                  onClick={redirectToMainUrl}
                  text={translations(APPEAL_TRANSLATIONS.header.login)}
                />
              ),
              secondCustomItem: (
                <ButtonInterface
                  fullWidth
                  onClick={redirectToMainUrl}
                  text={translations(APPEAL_TRANSLATIONS.header.register)}
                />
              ),
            },
          },
        },
      }}
      variant="default"
    />
  );
};

export default CustomHeader;
